import { create } from 'zustand'

const STORAGE_KEY = 'ti.photoSession'

interface PhotoSession {
  scope: string | null
  /** Random per-tab identity shared by direct uploads, the phone uploader and the listener. */
  photoIdent: string | null
  photoUrl: string | null
  /** Kept after X/add so an old upload cannot become the current photo again. */
  handledUrls: string[]
}

interface AddFlowState extends PhotoSession {
  startSession: (shopId: string, consumerId: string) => void
  setPhotoUrl: (url: string | null) => void
  receivePhotos: (photoIdent: string, photos: { url: string }[]) => string | null
  endSession: () => void
}

function restoreSession(scope: string): PhotoSession | null {
  try {
    const saved = JSON.parse(sessionStorage.getItem(STORAGE_KEY) ?? 'null')
    if (
      saved?.scope === scope &&
      typeof saved.photoIdent === 'string' && saved.photoIdent &&
      (saved.photoUrl === null || typeof saved.photoUrl === 'string') &&
      Array.isArray(saved.handledUrls) &&
      saved.handledUrls.every((url: unknown) => typeof url === 'string')
    ) {
      return {
        scope, photoIdent: saved.photoIdent, photoUrl: saved.photoUrl, handledUrls: saved.handledUrls,
      }
    }
  } catch {
    // Storage may be blocked or contain an incomplete record; start a fresh session.
  }
  return null
}

/**
 * The active photo survives navigation and reloads within this tab. Bind the saved record to the
 * authenticated shop/customer before rendering the flow, never to the shared magic-link token.
 * The receiver keeps its files; this store decides which one is still in the customer's draft.
 */
export const useAddFlowStore = create<AddFlowState>((set, get) => {
  const save = (session: PhotoSession) => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(session))
    } catch {
      // With storage unavailable, uploads still work for this page's lifetime.
    }
    set(session)
  }

  return {
    scope: null,
    photoIdent: null,
    photoUrl: null,
    handledUrls: [],
    startSession: (shopId, consumerId) => {
      const scope = JSON.stringify([shopId, consumerId])
      if (get().scope === scope) return
      save(restoreSession(scope) ?? {
        scope, photoIdent: crypto.randomUUID(), photoUrl: null, handledUrls: [],
      })
    },
    setPhotoUrl: (photoUrl) => {
      const { scope, photoIdent, handledUrls } = get()
      if (!scope || !photoIdent) return
      save({
        scope, photoIdent, photoUrl,
        handledUrls:
          photoUrl && !handledUrls.includes(photoUrl) ? [...handledUrls, photoUrl] : handledUrls,
      })
    },
    receivePhotos: (photoIdent, photos) => {
      const current = get()
      if (photoIdent !== current.photoIdent) return null // late callback after logout/account switch
      const fresh = photos
        .map((photo) => photo.url)
        .filter((url) => url && !current.handledUrls.includes(url))
      if (!fresh.length) return null
      const photoUrl = fresh[fresh.length - 1]
      save({
        scope: current.scope, photoIdent, photoUrl,
        handledUrls: [...new Set([...current.handledUrls, ...fresh])],
      })
      return photoUrl
    },
    endSession: () => {
      try {
        sessionStorage.removeItem(STORAGE_KEY)
      } catch {
        // Still clear in-memory state when storage is blocked.
      }
      set({ scope: null, photoIdent: null, photoUrl: null, handledUrls: [] })
    },
  }
})
